import React, { useState, useEffect, useRef } from 'react';
import { fetchUtils, usePermissions } from 'react-admin';
import DataTable from 'react-data-table-component';
import { Card, CardContent, TextField as MuiTextField, Button, Typography, Select,
  MenuItem, FormControl, InputLabel, CircularProgress, Chip, Paper } from '@material-ui/core';
import SendIcon from '@material-ui/icons/Send';
import CloudDownload from '@material-ui/icons/CloudDownload';
import ReactMarkdown from 'react-markdown';
const ReactHighcharts = require('react-highcharts');

const fetch = fetchUtils.fetchJson;
const BASE_URL = window.nobid_endpoint + '/gam-chat';

const SUGGESTED_QUERIES = [
  { name: 'Revenue last 30 days', prompt: 'Daily revenue, impressions and CPM for the last 30 days' },
  { name: 'Revenue by ad unit', prompt: 'Revenue and impressions by ad unit last 7 days' },
  { name: 'Orders this month', prompt: 'Impressions and revenue by order this month' },
  { name: 'Fill rate', prompt: 'Fill rate, ad requests and responses served by ad unit yesterday' }
];

function formatNumber(val) {
  if (val === null || val === undefined || val === '') return '-';
  if (typeof val === 'number') return val.toLocaleString();
  return val;
}

function buildColumns(rows) {
  if (!rows || rows.length === 0) return [];
  const keys = Object.keys(rows[0]);
  return keys.map(key => ({
    name: key.replace(/_/g, ' '),
    selector: row => row[key],
    sortable: true,
    cell: row => <span>{formatNumber(row[key])}</span>,
    wrap: true
  }));
}

function exportCSV(rows, metadata) {
  if (!rows || rows.length === 0) return;
  const keys = Object.keys(rows[0]);
  const header = keys.join(',');
  const body = rows.map(row => keys.map(k => {
    const val = row[k];
    if (typeof val === 'string' && val.includes(',')) return `"${val}"`;
    return val;
  }).join(',')).join('\n');
  const csv = header + '\n' + body;
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `gam_report_${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

function getChartableMetrics(rows, metadata) {
  if (!rows || rows.length === 0 || !metadata) return [];
  // All numeric keys in the rows (includes computed eCPM)
  return Object.keys(rows[0]).filter(k => typeof rows[0][k] === 'number');
}

function buildChartConfig(rows, metadata, selectedMetric) {
  if (!rows || rows.length < 2 || !metadata) return null;

  const dims = metadata.dimensions || [];
  const chartableMetrics = getChartableMetrics(rows, metadata);
  if (chartableMetrics.length === 0) return null;

  const metricKey = selectedMetric && chartableMetrics.includes(selectedMetric) ? selectedMetric : chartableMetrics[0];
  const metricLabel = (metricKey || '').replace(/_/g, ' ');

  // Time series: DATE dimension → line chart
  if (dims.includes('DATE') && rows[0].DATE) {
    const sorted = [...rows].sort((a, b) => String(a.DATE).localeCompare(String(b.DATE)));
    return {
      chart: { type: 'line', height: 250, backgroundColor: 'transparent' },
      title: { text: null },
      xAxis: { categories: sorted.map(r => {
        const d = new Date(r.DATE + 'T00:00:00');
        const day = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'][d.getDay()];
        return `${day} ${r.DATE.slice(5)}`;
      }), labels: { rotation: -45, style: { fontSize: '10px' } } },
      yAxis: { title: { text: null } },
      legend: { enabled: false },
      credits: { enabled: false },
      series: [{ name: metricLabel, data: sorted.map(r => r[metricKey] || 0), color: '#1976d2' }]
    };
  }

  // Dimension breakdown → horizontal bar chart (top 15)
  const dimKey = Object.keys(rows[0]).find(k => typeof rows[0][k] === 'string') || dims[0];
  if (!dimKey) return null;

  const top = rows.slice(0, 15);
  return {
    chart: { type: 'bar', height: Math.max(200, top.length * 28), backgroundColor: 'transparent' },
    title: { text: null },
    xAxis: { categories: top.map(r => String(r[dimKey] || '').substring(0, 40)), labels: { style: { fontSize: '11px' } } },
    yAxis: { title: { text: null } },
    legend: { enabled: false },
    credits: { enabled: false },
    plotOptions: { bar: { dataLabels: { enabled: true, formatter: function() { return this.y.toLocaleString(); } } } },
    series: [{ name: metricLabel, data: top.map(r => r[metricKey] || 0), color: '#1976d2' }]
  };
}

const ChatMessage = React.memo(function ChatMessage({ msg }) {
  const isUser = msg.role === 'user';
  const [chartMetric, setChartMetric] = useState(null);

  const chartableMetrics = !isUser && msg.rows && msg.rows.length > 0 ? getChartableMetrics(msg.rows, msg.metadata) : [];

  return (
    <div style={{
      display: 'flex',
      justifyContent: isUser ? 'flex-end' : 'flex-start',
      marginBottom: 12
    }}>
      <Paper elevation={1} style={{
        padding: '12px 16px',
        maxWidth: '80%',
        backgroundColor: isUser ? '#1976d2' : '#f5f5f5',
        color: isUser ? '#fff' : '#333',
        borderRadius: isUser ? '16px 16px 4px 16px' : '16px 16px 16px 4px'
      }}>
        {isUser ? (
          <Typography variant="body1">{msg.text}</Typography>
        ) : (
          <ReactMarkdown>{msg.text}</ReactMarkdown>
        )}
        {msg.rows && msg.rows.length > 0 && (() => {
          const chartConfig = buildChartConfig(msg.rows, msg.metadata, chartMetric);
          return (
          <div style={{ marginTop: 12 }}>
            {chartConfig && (
              <div style={{ marginBottom: 12, background: '#fff', borderRadius: 4, padding: 8 }}>
                {chartableMetrics.length > 1 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 8 }}>
                    {chartableMetrics.map(m => (
                      <Chip
                        key={m}
                        label={m.replace(/_/g, ' ').replace(/TOTAL LINE ITEM LEVEL /g, '')}
                        size="small"
                        color={(chartMetric || chartableMetrics[0]) === m ? 'primary' : 'default'}
                        onClick={() => setChartMetric(m)}
                        clickable
                      />
                    ))}
                  </div>
                )}
                <ReactHighcharts config={chartConfig} />
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
              <Button
                size="small"
                startIcon={<CloudDownload />}
                onClick={() => exportCSV(msg.rows, msg.metadata)}
                style={{ color: '#666' }}
              >
                Export CSV
              </Button>
            </div>
            <DataTable
              columns={buildColumns(msg.rows)}
              data={msg.rows}
              dense
              pagination
              paginationPerPage={10}
              highlightOnHover
              theme={window.nobid_table_theme ? window.nobid_table_theme() : undefined}
            />
          </div>
          );
        })()}
      </Paper>
    </div>
  );
});

export function GamChatPage() {
  const { permissions } = usePermissions();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [gamAccounts, setGamAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState('');
  const chatEndRef = useRef(null);
  const isAdmin = permissions && (permissions.role === 'admin' || permissions.role === 'super_admin');

  // Load GAM accounts the current user can access
  useEffect(() => {
    const token = localStorage.getItem('token');
    fetch(`${window.nobid_endpoint}/gam-chat/accounts`, {
      headers: new Headers({ Authorization: `Bearer ${token}` })
    }).then(({ json }) => {
      let accounts = json || [];
      // Admins: filter to active accounts
      if (isAdmin) {
        const ALLOWED_ACCOUNTS = ['techcrunch', 'cheddar', 'opsco', 'nobid', 'carbon_mediatonik', 'ein', 'duration'];
        accounts = accounts.filter(acc => ALLOWED_ACCOUNTS.includes(acc.api_id));
      }
      setGamAccounts(accounts);
      if (accounts.length > 0 && !selectedAccount) {
        setSelectedAccount(accounts[0].api_id);
      }
    }).catch(() => {});
  }, [permissions]);

  // Scroll to bottom on new messages
  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  async function sendMessage(text) {
    if (!text || !text.trim() || loading) return;

    const userMsg = { role: 'user', text: text.trim() };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      // Build conversation history for Haiku context — include summary + sample rows
      const history = messages.map(m => {
        if (m.role === 'user') return { role: 'user', content: m.text };
        // Include summary + first 20 rows so Haiku can answer data questions
        const context = { summary: m.text };
        if (m.rows && m.rows.length > 0) {
          context.rows = m.rows.slice(0, 20);
          context.rowCount = m.rows.length;
        }
        return { role: 'assistant', content: JSON.stringify(context) };
      });
      const body = { message: text.trim(), history };
      if (selectedAccount) {
        body.gamAccount = selectedAccount;
      }

      const { json } = await fetch(BASE_URL, {
        method: 'POST',
        headers: new Headers({
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }),
        body: JSON.stringify(body)
      });

      const assistantMsg = {
        role: 'assistant',
        text: json.summary || 'No response received.',
        rows: json.rows || [],
        metadata: json.metadata || null
      };
      setMessages(prev => [...prev, assistantMsg]);
    } catch (err) {
      const errorMsg = {
        role: 'assistant',
        text: 'Sorry, something went wrong. Please try again.',
        rows: [],
        metadata: null
      };
      setMessages(prev => [...prev, errorMsg]);
    } finally {
      setLoading(false);
    }
  }

  function handleKeyPress(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(input);
    }
  }

  return (
    <Card style={{ margin: 16, height: 'calc(100vh - 120px)', display: 'flex', flexDirection: 'column' }}>
      {/* Header with optional account picker */}
      <div style={{ padding: '16px 16px 8px', borderBottom: '1px solid #eee', display: 'flex', alignItems: 'center', gap: 16 }}>
        <Typography variant="h6" style={{ flex: 1 }}>GAM Chat</Typography>
        {gamAccounts.length > 1 && (
          <FormControl variant="outlined" size="small" style={{ minWidth: 200 }}>
            <InputLabel>GAM Account</InputLabel>
            <Select
              value={selectedAccount}
              onChange={(e) => setSelectedAccount(e.target.value)}
              label="GAM Account"
            >
              {gamAccounts.map(acc => {
                const DISPLAY_NAMES = { cheddar: 'Rate My Professors' };
                const label = DISPLAY_NAMES[acc.api_id] || acc.name || acc.api_id;
                return <MenuItem key={acc.api_id} value={acc.api_id}>{label}</MenuItem>;
              })}
            </Select>
          </FormControl>
        )}
      </div>

      {/* Chat messages area */}
      <CardContent style={{ flex: 1, overflow: 'auto', padding: 16 }}>
        {messages.length === 0 && !loading && (
          <div style={{ textAlign: 'center', paddingTop: 40 }}>
            <Typography variant="h6" color="textSecondary" gutterBottom>
              Ask anything about your GAM data
            </Typography>
            <Typography variant="body2" color="textSecondary" style={{ marginBottom: 24 }}>
              Try one of these:
            </Typography>
            <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 8 }}>
              {SUGGESTED_QUERIES.map((q, i) => (
                <Chip
                  key={i}
                  label={q.name}
                  clickable
                  onClick={() => sendMessage(q.prompt)}
                  variant="outlined"
                  color="primary"
                />
              ))}
            </div>
          </div>
        )}

        {messages.map((msg, i) => <ChatMessage key={i} msg={msg} />)}

        {loading && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 0' }}>
            <CircularProgress size={20} />
            <Typography variant="body2" color="textSecondary">Running report...</Typography>
          </div>
        )}

        <div ref={chatEndRef} />
      </CardContent>

      {/* Input bar */}
      <div style={{ padding: 16, borderTop: '1px solid #eee', display: 'flex', gap: 8 }}>
        <MuiTextField
          fullWidth
          variant="outlined"
          size="small"
          placeholder="Ask about your GAM data..."
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyPress={handleKeyPress}
          disabled={loading}
          autoFocus
        />
        <Button
          variant="contained"
          color="primary"
          onClick={() => sendMessage(input)}
          disabled={loading || !input.trim()}
        >
          <SendIcon />
        </Button>
      </div>
    </Card>
  );
}

// react-admin requires a List export for Resource registration
export const GamChatList = GamChatPage;
